import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    legacyApi,
    SupplierBill,
    SupplierBillDetail as BillDetail,
    WorkflowEntityDefinition,
    WorkflowEventDefinition,
    WorkflowHistoryEntry,
} from '../../../services/legacyApi';
import { Button } from '../../../components/ui/button';
import { CompactTable } from '../../../components/ui/CompactTable';
import { ArrowLeft, Loader2, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { getSupplierBillStatusMeta } from '../utils/statuses';
import { toast } from 'sonner';
import { TransitionActionBar } from '../components/TransitionActionBar';
import { StatusGuidePanel } from '../components/StatusGuidePanel';
import { StatusHistoryPanel } from '../components/StatusHistoryPanel';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { useAuth } from '../../../context/AuthContext';
import { api } from '../../../services/api';
import type { Account } from '../../../services/types';

interface ExtendedBill extends SupplierBill {
    details?: BillDetail[];
}

export const LegacySupplierBillDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [bill, setBill] = useState<ExtendedBill | null>(null);
    const [loading, setLoading] = useState(true);
    const [transitionBusy, setTransitionBusy] = useState<string | null>(null);
    const [vatReviewBusy, setVatReviewBusy] = useState(false);
    const [vatRecoveryPercent, setVatRecoveryPercent] = useState(100);
    const [nonrecoverableVatAccountId, setNonrecoverableVatAccountId] = useState('');
    const [vatDestinationAccounts, setVatDestinationAccounts] = useState<Account[]>([]);
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);
    const [editMode, setEditMode] = useState(false);
    const [editBillDate, setEditBillDate] = useState('');
    const [editReferenceNo, setEditReferenceNo] = useState('');
    const [editRemarks, setEditRemarks] = useState('');
    const [editDiscountAmount, setEditDiscountAmount] = useState(0);
    const [editVatPercent, setEditVatPercent] = useState(5);
    const [editDetails, setEditDetails] = useState<Array<{ bill_detail_id: number; quantity: number; unit_price: number }>>([]);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });
    const supplierBillLockedMessage = 'Only pending supplier bills can be edited. Cancel the linked bill first, then create/update the corrected bill.';

    const loadPage = async (billId: number) => {
        const [billData, definition, actions, historyResult] = await Promise.all([
            legacyApi.purchasing.getBill(billId),
            legacyApi.workflow.getDefinition('SUPPLIER_BILL').catch(() => null),
            legacyApi.workflow.getNextActions('SUPPLIER_BILL', billId).catch(() => ({ next_actions: [] })),
            legacyApi.workflow.getHistory('SUPPLIER_BILL', billId).catch(() => ({ history: [] })),
        ]);
        setBill(billData);
        setEditBillDate((billData.bill_date || '').slice(0, 10));
        setEditReferenceNo(billData.reference_no || '');
        setEditRemarks(billData.remarks || '');
        setEditDiscountAmount(Number(billData.discount_amount || 0));
        setEditVatPercent(Number(billData.vat_percent || 0));
        setVatRecoveryPercent(Number(billData.input_vat_recovery_percent ?? 100));
        setNonrecoverableVatAccountId(billData.nonrecoverable_vat_account_id ? String(billData.nonrecoverable_vat_account_id) : '');
        setEditDetails(
            (billData.details || []).map((d: any) => ({
                bill_detail_id: Number(d.bill_detail_id),
                quantity: Number(d.quantity || 0),
                unit_price: Number(d.unit_price || 0),
            })),
        );
        setWorkflowDefinition(definition);
        setNextActions((actions as any).next_actions || []);
        setHistory((historyResult as any).history || []);
    };

    useEffect(() => {
        if (!id) return;
        if (id === 'new') {
            navigate('/purchasing/bills');
            return;
        }

        const billId = parseInt(id, 10);
        setLoading(true);
        loadPage(billId)
            .catch((err) => {
                console.error(err);
                toast.error(err?.message || 'Failed to load supplier bill');
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    useEffect(() => {
        if (!hasPermission('finance.accounts.manage')) return;
        api.financials.listAccounts()
            .then((rows) => setVatDestinationAccounts(rows.filter((account) =>
                account.is_active !== false && account.has_children !== true && ['Asset', 'Expense'].includes(account.type)
            )))
            .catch(() => setVatDestinationAccounts([]));
    }, [hasPermission]);

    useEffect(() => {
        if (editMode && bill && Number(bill.status_id || 1) !== 1) {
            setEditMode(false);
        }
    }, [bill?.status_id, editMode]);

    const handleTransition = async (action: string) => {
        if (!bill) return;
        setTransitionBusy(action);
        try {
            await legacyApi.purchasing.transitionBill(bill.bill_id, action, 'Updated from supplier bill detail page');
            toast.success('Supplier bill status updated');
            await loadPage(bill.bill_id);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update supplier bill status');
        } finally {
            setTransitionBusy(null);
        }
    };

    const handleRecordPayment = () => {
        if (!bill) return;
        const query = new URLSearchParams();
        query.set('tab', 'ap');
        query.set('mode', 'BILL_SETTLEMENT');
        query.set('bill_id', String(bill.bill_id));
        if (bill.supplier_id) query.set('supplier_id', String(bill.supplier_id));
        const outstanding = Number(bill.outstanding_amount ?? bill.grand_total ?? 0);
        if (outstanding > 0) query.set('amount', outstanding.toFixed(2));
        navigate(`/financials?${query.toString()}`);
    };

    const handleVatRecoveryReview = async () => {
        if (!bill) return;
        if (vatRecoveryPercent < 0 || vatRecoveryPercent > 100) {
            toast.error('Recovery percentage must be between 0 and 100');
            return;
        }
        if (vatRecoveryPercent < 100 && !nonrecoverableVatAccountId) {
            toast.error('Select the Asset or Expense account for non-recoverable VAT');
            return;
        }
        try {
            setVatReviewBusy(true);
            await legacyApi.purchasing.reviewBillVat(bill.bill_id, {
                recovery_percent: vatRecoveryPercent,
                vat_treatment: bill.vat_treatment || 'STANDARD',
                nonrecoverable_vat_account_id: nonrecoverableVatAccountId ? Number(nonrecoverableVatAccountId) : null,
            });
            toast.success('Input VAT recovery confirmed');
            await loadPage(bill.bill_id);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to review Input VAT recovery');
        } finally {
            setVatReviewBusy(false);
        }
    };

    const handleSaveBillEdits = async () => {
        if (!bill) return;
        if (Number(bill.status_id || 1) !== 1) {
            toast.error('Posted or cancelled supplier bills are locked. Cancel the linked bill first, then create/update the corrected bill.');
            setEditMode(false);
            return;
        }
        if (editDiscountAmount < 0) {
            toast.error('Discount cannot be negative');
            return;
        }
        if (editVatPercent < 0 || editVatPercent > 100) {
            toast.error('VAT % must be between 0 and 100');
            return;
        }
        try {
            setLoading(true);
            const updated = await legacyApi.purchasing.updateBill(bill.bill_id, {
                bill_date: editBillDate || undefined,
                reference_no: editReferenceNo || undefined,
                remarks: editRemarks || undefined,
                discount_amount: editDiscountAmount,
                vat_percent: editVatPercent,
                details: editDetails.map((line) => ({
                    bill_detail_id: Number(line.bill_detail_id),
                    quantity: Number(line.quantity),
                    unit_price: Number(line.unit_price),
                })),
            });
            setBill(updated);
            setEditMode(false);
            toast.success('Supplier bill updated');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update supplier bill');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { header: 'Material', accessor: (item: any) => item.description || `Item #${item.material_id}`, className: 'font-medium' },
        {
            header: 'Qty',
            accessor: (item: any) => {
                if (!editMode) return item.quantity;
                const row = editDetails.find((d) => Number(d.bill_detail_id) === Number(item.bill_detail_id));
                return (
                    <input
                        type="number"
                        min={0}
                        value={row?.quantity ?? 0}
                        className="h-8 w-24 rounded border px-2 text-right"
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            setEditDetails((prev) => prev.map((d) => (
                                Number(d.bill_detail_id) === Number(item.bill_detail_id)
                                    ? { ...d, quantity: Number.isFinite(value) ? value : 0 }
                                    : d
                            )));
                        }}
                    />
                );
            },
            className: 'text-right'
        },
        {
            header: 'Unit Price',
            accessor: (item: any) => {
                if (!editMode) return item.unit_price?.toFixed(2);
                const row = editDetails.find((d) => Number(d.bill_detail_id) === Number(item.bill_detail_id));
                return (
                    <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row?.unit_price ?? 0}
                        className="h-8 w-28 rounded border px-2 text-right"
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            setEditDetails((prev) => prev.map((d) => (
                                Number(d.bill_detail_id) === Number(item.bill_detail_id)
                                    ? { ...d, unit_price: Number.isFinite(value) ? value : 0 }
                                    : d
                            )));
                        }}
                    />
                );
            },
            className: 'text-right'
        },
        {
            header: 'Sub Total',
            accessor: (item: any) => {
                if (!editMode) return item.sub_total?.toFixed(2);
                const row = editDetails.find((d) => Number(d.bill_detail_id) === Number(item.bill_detail_id));
                const value = Number(row?.quantity || 0) * Number(row?.unit_price || 0);
                return value.toFixed(2);
            },
            className: 'text-right font-medium'
        },
    ];
    const filteredDetails = useMemo(() => {
        const details = bill?.details || [];
        const query = state.q.trim().toLowerCase();
        if (!query) return details;
        return details.filter((item: any) =>
            String(item.description || '').toLowerCase().includes(query) ||
            String(item.material_id || '').toLowerCase().includes(query)
        );
    }, [bill?.details, state.q]);
    const headerTransitionActions = useMemo(
        () =>
            (nextActions || []).filter((action) => {
                const code = String(action.event_code || '').trim().toLowerCase();
                return code === 'post' || code === 'cancel';
            }),
        [nextActions],
    );

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (!bill) return <div className="p-8 text-center text-red-500">Bill not found</div>;

    const statusMeta = getSupplierBillStatusMeta(bill.status_id);
    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === bill.status_id);
    const paidAmount = Number(bill.paid_amount || 0);
    const outstandingAmount = Number(bill.outstanding_amount ?? bill.grand_total ?? 0);
    const paymentProgress =
        bill.payment_progress ||
        (outstandingAmount <= 0 ? 'Fully Paid' : paidAmount > 0 ? 'Partially Paid' : 'Not Paid');

    const canEditBill = bill.status_id === 1 && hasPermission('purchasing.bill.edit');
    const billLocked = Number(bill.status_id || 1) !== 1;

    return (
        <div className="space-y-6 w-full p-6">
            <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" onClick={() => navigate('/purchasing/bills')}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bills
                </Button>
                <h1 className="text-2xl font-bold">Supplier Bill #{bill.bill_id}</h1>
                <Button
                    variant="outline"
                    onClick={handleRecordPayment}
                    disabled={!bill.supplier_id || outstandingAmount <= 0}
                    title={!bill.supplier_id ? 'Supplier link is missing on this bill' : undefined}
                >
                    <Wallet className="h-4 w-4 mr-2" /> Record Payment
                </Button>
                {canEditBill && !editMode && (
                    <Button variant="outline" onClick={() => setEditMode(true)}>
                        Edit Bill
                    </Button>
                )}
                {editMode && (
                    <>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setEditMode(false);
                                setEditDetails(
                                    (bill.details || []).map((d: any) => ({
                                        bill_detail_id: Number(d.bill_detail_id),
                                        quantity: Number(d.quantity || 0),
                                        unit_price: Number(d.unit_price || 0),
                                    })),
                                );
                            }}
                        >
                            Cancel Edit
                        </Button>
                        <Button onClick={handleSaveBillEdits} disabled={loading}>Save Changes</Button>
                    </>
                )}
                {headerTransitionActions.length > 0 && (
                    <>
                        <div className="mx-1 h-6 w-px bg-border" />
                        <TransitionActionBar
                            actions={headerTransitionActions}
                            onAction={handleTransition}
                            disabled={transitionBusy !== null}
                            busyEventCode={transitionBusy}
                            precheckEntity="SUPPLIER_BILL"
                            precheckEntityId={bill.bill_id}
                            actionOrder={['post', 'cancel']}
                        />
                    </>
                )}
            </div>

            {billLocked && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <div className="font-semibold">Supplier bill is locked for accounting control.</div>
                    <div>{supplierBillLockedMessage}</div>
                </div>
            )}

            {Number(bill.vat_amount || 0) !== 0 && (
                <Card>
                    <CardHeader><CardTitle>Input VAT Recovery</CardTitle></CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-[minmax(0,180px),minmax(0,1fr),auto] md:items-end">
                        <label className="space-y-2 text-sm">
                            <span className="font-medium">Recoverable %</span>
                            <input
                                type="number"
                                min={0}
                                max={100}
                                step="0.01"
                                className="h-10 w-full rounded-md border px-3"
                                value={vatRecoveryPercent}
                                onChange={(event) => setVatRecoveryPercent(Number(event.target.value))}
                                disabled={!hasPermission('finance.accounts.manage')}
                            />
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium">Non-Recoverable VAT Account</span>
                            <select
                                className="h-10 w-full rounded-md border bg-white px-3"
                                value={nonrecoverableVatAccountId}
                                onChange={(event) => setNonrecoverableVatAccountId(event.target.value)}
                                disabled={!hasPermission('finance.accounts.manage') || vatRecoveryPercent >= 100}
                            >
                                <option value="">Not required for 100% recovery</option>
                                {vatDestinationAccounts.map((account) => (
                                    <option key={account.id} value={account.id}>{account.code} - {account.name}</option>
                                ))}
                            </select>
                        </label>
                        <Button
                            onClick={() => void handleVatRecoveryReview()}
                            disabled={!hasPermission('finance.accounts.manage') || vatReviewBusy || bill.source_type === 'BILL_RETURN'}
                        >
                            {vatReviewBusy ? 'Saving...' : 'Confirm Recovery'}
                        </Button>
                        <div className="text-sm text-muted-foreground md:col-span-3">
                            Status: <strong>{bill.vat_recovery_status || 'UNREVIEWED'}</strong>
                            {' | '}Recoverable VAT: {Number(bill.recoverable_vat_amount || 0).toFixed(2)}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-6">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader><CardTitle>Bill Details</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Supplier</span>
                                    <span className="font-medium">{bill.supplier_name}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Reference No</span>
                                    <span className="font-medium">
                                        {editMode ? (
                                            <input
                                                value={editReferenceNo}
                                                onChange={(e) => setEditReferenceNo(e.target.value)}
                                                className="h-8 rounded border px-2 text-sm"
                                            />
                                        ) : (
                                            bill.reference_no || '-'
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Bill Date</span>
                                    <span className="font-medium">
                                        {editMode ? (
                                            <input
                                                type="date"
                                                value={editBillDate}
                                                onChange={(e) => setEditBillDate(e.target.value)}
                                                className="h-8 rounded border px-2 text-sm"
                                            />
                                        ) : (
                                            bill.bill_date ? new Date(bill.bill_date).toLocaleDateString() : '-'
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Linked GRN</span>
                                    <span className="font-medium">
                                        {bill.grn_id ? (
                                            <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/legacy/grn/${bill.grn_id}`)}>
                                                HC/GRN/{String(bill.grn_id).padStart(4, '0')}
                                            </Button>
                                        ) : '-'}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Status</span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${statusMeta.badgeClass}`}>
                                        {statusMeta.label}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Financials</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Sub Total</span>
                                    <span className="font-medium">{bill.sub_total?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">VAT ({editMode ? editVatPercent : bill.vat_percent}%)</span>
                                    <span className="font-medium">
                                        {editMode ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    value={editVatPercent}
                                                    onChange={(e) => setEditVatPercent(Number(e.target.value))}
                                                    className="h-8 w-20 rounded border px-2 text-sm text-right"
                                                />
                                                <span>{bill.vat_amount?.toFixed(2)}</span>
                                            </div>
                                        ) : (
                                            bill.vat_amount?.toFixed(2)
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Discount</span>
                                    <span className="font-medium">
                                        {editMode ? (
                                            <input
                                                type="number"
                                                min={0}
                                                value={editDiscountAmount}
                                                onChange={(e) => setEditDiscountAmount(Number(e.target.value))}
                                                className="h-8 w-24 rounded border px-2 text-sm text-right"
                                            />
                                        ) : (
                                            Number(bill.discount_amount || 0).toFixed(2)
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Grand Total</span>
                                    <span className="font-bold text-lg">{bill.grand_total?.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Paid</span>
                                    <span className="font-medium">{paidAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Outstanding</span>
                                    <span className="font-medium">{outstandingAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Payment Progress</span>
                                    <span className="font-medium">{paymentProgress}</span>
                                </div>
                                <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 text-sm rounded">
                                    Remarks:{' '}
                                    {editMode ? (
                                        <input
                                            value={editRemarks}
                                            onChange={(e) => setEditRemarks(e.target.value)}
                                            className="mt-2 h-8 w-full rounded border px-2 text-sm text-black"
                                        />
                                    ) : (
                                        bill.remarks || '-'
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Line Items</CardTitle></CardHeader>
                        <CardContent>
                            <TableFilterBar
                                searchValue={state.q}
                                onSearchChange={(value) => setFilter('q', value)}
                                searchPlaceholder="Search bill line items..."
                                isDirty={isDirty}
                                onClear={clearAll}
                                rightSlot={`${filteredDetails.length} results`}
                            />
                            <div className="mt-3">
                                <CompactTable data={filteredDetails} columns={columns} keyField="bill_detail_id" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <WorkflowRightSidebar mode="overlay" title="Supplier Bill Workflow & Status">
                    <WorkflowRightPanel
                        title="Workflow & Status"
                        description="Current status, next action, and prechecks"
                    >
                        <div className="text-sm text-muted-foreground">
                            Status:{' '}
                            <span className={`px-2 py-1 rounded-full text-xs ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            Payment Progress:{' '}
                            <span className="font-medium text-foreground">{paymentProgress}</span>
                        </div>
                        <TransitionActionBar
                            actions={nextActions}
                            onAction={handleTransition}
                            disabled={transitionBusy !== null}
                            busyEventCode={transitionBusy}
                            precheckEntity="SUPPLIER_BILL"
                            precheckEntityId={bill.bill_id}
                            hiddenEventCodes={['post', 'cancel']}
                        />
                        <StatusGuidePanel
                            statusLabel={statusDefinition?.status_label || statusMeta.label}
                            statusMeaning={statusDefinition?.status_meaning || ''}
                            nextActions={nextActions}
                        />
                        <FlowCoachPanel entity="SUPPLIER_BILL" entityId={bill.bill_id} />
                    </WorkflowRightPanel>
                    <WorkflowRightPanel
                        title="Status History"
                        description="Audit log of status transitions"
                        defaultOpen={false}
                    >
                        <StatusHistoryPanel history={history} />
                    </WorkflowRightPanel>
                </WorkflowRightSidebar>
            </div>
        </div>
    );
};
